import { Component, DestroyRef, inject, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PaymentMethod } from '../../../smoothr-web-app-core/enums/PaymentMethod';
import { PaySessionStatistics } from '../../../smoothr-web-app-core/models/PaySessionStatistics';
import Order from '../../../smoothr-web-app-core/models/Order';
import { AdyenCredentials } from '../../../smoothr-web-app-core/models/Venue';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import PiggyGiftCard from '../../../smoothr-web-app-core/models/PiggyGiftCard';
import { AddGiftCardModalComponent } from '../add-gift-card-modal/add-gift-card-modal.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-select-payment-method-modal',
	standalone: false,
	templateUrl: './select-payment-method-modal.component.html',
	styleUrls: ['./select-payment-method-modal.component.scss'],
})
export class SelectPaymentMethodModalComponent {
	@Input() paymentMethods: PaymentMethod[] = [];
	@Input() order: Order | null = null;
	@Input() adyenCredentials: AdyenCredentials | null = null;
	paySessionStatistics: PaySessionStatistics | null = null;
	addedGiftCards: PiggyGiftCard[] = [];
	selectedPaymentMethod: PaymentMethod | null = null;
	private repositoryService = inject(RepositoryService);
	private destroyRef = inject(DestroyRef);
	private translate = inject(TranslateService);
	private modalCtrl: ModalController = inject(ModalController);

	constructor() {
		this.repositoryService.paySessionStatisticsObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(statistics => {
			this.paySessionStatistics = statistics;
			console.log(statistics);
		});
		this.repositoryService.addedGiftCards.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(giftCards => {
			this.addedGiftCards = giftCards;
		});
	}

	/** Whether the venue supports gift cards */
	get supportsGiftCards(): boolean {
		return this.paymentMethods.includes(PaymentMethod.PIGGY_GIFT_CARD);
	}

	/** Payment methods to show in the radio list (excluding gift card, which is shown separately) */
	get filteredPaymentMethods(): PaymentMethod[] {
		return this.paymentMethods.filter(m => m !== PaymentMethod.PIGGY_GIFT_CARD);
	}

	get totalGiftCardBalance(): number {
		return this.addedGiftCards.reduce((sum, card) => sum + card.balance, 0);
	}

	get formattedGiftCardBalance(): string {
		return this.repositoryService.currencyFormat.format(this.totalGiftCardBalance);
	}

	get checkAmount(): number {
		return this.paySessionStatistics?.selectedTotalPrice ?? 0;
	}

	get remainingAfterGiftCards(): number {
		return Math.max(0, +(this.checkAmount - this.totalGiftCardBalance).toFixed(2));
	}

	get formattedRemaining(): string {
		return this.repositoryService.currencyFormat.format(this.remainingAfterGiftCards);
	}

	get giftCardsFullyCoverCheck(): boolean {
		return this.addedGiftCards.length > 0 && this.totalGiftCardBalance >= this.checkAmount;
	}

	/** Can proceed: either gift cards cover everything, or a secondary method is selected */
	get canConfirm(): boolean {
		if (this.giftCardsFullyCoverCheck) {
			return true;
		}
		return !!this.selectedPaymentMethod;
	}

	static async show(
		modalCtrl: ModalController,
		paySessionStatistics: PaySessionStatistics | null,
		paymentMethods: PaymentMethod[],
		order: Order,
		adyenCredentials: AdyenCredentials
	): Promise<PaymentMethod> {
		const modal = await modalCtrl.create({
			component: SelectPaymentMethodModalComponent,
			showBackdrop: true,
			backdropDismiss: true,
			cssClass: 'payment-sheet-modal',
			componentProps: {
				paymentMethods,
				paySessionStatistics,
				order,
				adyenCredentials,
			},
		});

		await modal.present();

		const { data } = await modal.onDidDismiss();
		return data;
	}

	selectMethod(paymentMethod: PaymentMethod) {
		this.selectedPaymentMethod = paymentMethod;
	}

	confirmSelection() {
		if (this.giftCardsFullyCoverCheck) {
			// Gift cards cover the entire check — signal gift card payment
			this.modalCtrl.dismiss(PaymentMethod.PIGGY_GIFT_CARD);
			return;
		}
		if (this.selectedPaymentMethod) {
			this.modalCtrl.dismiss(this.selectedPaymentMethod);
		}
	}

	label(paymentMethod: PaymentMethod): string {
		switch (paymentMethod) {
			case PaymentMethod.CASH:
				return this.translate.instant('paymentMethod.cash');
			case PaymentMethod.ADYEN:
				return this.translate.instant('paymentMethod.adyen');
			case PaymentMethod.APAY:
				return this.translate.instant('paymentMethod.apay');
			case PaymentMethod.GPAY:
				return this.translate.instant('paymentMethod.gpay');
			case PaymentMethod.PAYPAL_ADYEN:
			case PaymentMethod.PAYPAL:
				return this.translate.instant('paymentMethod.paypal');
			case PaymentMethod.PIGGY_GIFT_CARD:
				return this.translate.instant('paymentMethod.giftCard');
			default:
				return paymentMethod.toString();
		}
	}

	icon(paymentMethod: PaymentMethod): string {
		switch (paymentMethod) {
			case PaymentMethod.PIGGY_GIFT_CARD:
			case PaymentMethod.CASH:
				return 'assets/icon/cash.svg';
			case PaymentMethod.ADYEN:
				return 'assets/icon/cards.svg';
			case PaymentMethod.APAY:
				return 'assets/icon/apple-pay.svg';
			case PaymentMethod.GPAY:
				return 'assets/icon/google-pay.png';
			case PaymentMethod.PAYPAL:
			case PaymentMethod.PAYPAL_ADYEN:
				return 'assets/icon/paypal.svg';
			default:
				return paymentMethod.toString();
		}
	}

	close() {
		this.repositoryService.clearGeneratedOrder();
		this.modalCtrl.dismiss();
	}

	async addGiftCard() {
		const result = await AddGiftCardModalComponent.show(this.modalCtrl);
		if (result?.giftCard) {
			this.repositoryService.addGiftCard(result.giftCard);
		}
	}

	removeGiftCard(hash: string) {
		this.repositoryService.removeGiftCard(hash);
	}

	formattedCardBalance(card: PiggyGiftCard): string {
		return this.repositoryService.currencyFormat.format(card.balance);
	}
}
